import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { ApiError } from './errors';
import { getRefreshToken, setRefreshToken } from '@/lib/tokens';
import type { AuthTokens } from '@/types/api';

// Access token lives only in memory (React Context).
// Refresh token is persisted in localStorage (lib/tokens).
type AuthBindings = {
  getAccess: () => string | null;
  setAccess: (token: string | null) => void;
  onFailure: () => void;
};

const noop = () => {};
let bindings: AuthBindings = {
  getAccess: () => null,
  setAccess: noop,
  onFailure: noop,
};

export function bindAuth(next: AuthBindings) {
  bindings = next;
}

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export const api = axios.create({
  baseURL,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

// Bare axios instance for /auth/refresh: bypass interceptors to avoid recursion.
const refreshApi = axios.create({ baseURL, timeout: 15_000 });

// Attach Authorization header on every outgoing request.
api.interceptors.request.use((config) => {
  const token = bindings.getAccess();
  if (token && !config.headers?.['X-Skip-Auth']) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  if (config.headers?.['X-Skip-Auth']) {
    delete (config.headers as Record<string, string>)['X-Skip-Auth'];
  }
  return config;
});

// Refresh state: only one refresh in flight; queue concurrent 401s.
type QueuedRequest = {
  resolve: (token: string) => void;
  reject: (err: ApiError) => void;
};

let isRefreshing = false;
let pendingQueue: QueuedRequest[] = [];

function flushQueue(error: ApiError | null, token: string | null) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else if (token) resolve(token);
    else reject(new ApiError({ status: 0, message: 'No access token after refresh' }));
  });
  pendingQueue = [];
}

async function runRefresh(): Promise<AuthTokens> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new ApiError({ status: 401, message: 'No refresh token available' });
  }
  try {
    const res = await refreshApi.post<AuthTokens>('/auth/refresh', { refreshToken });
    return res.data;
  } catch (err) {
    throw ApiError.from(err);
  }
}

api.interceptors.response.use(
  (res: AxiosResponse) => res,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;
    const status = error.response?.status;

    const isAuthRoute =
      original?.url?.includes('/auth/login') || original?.url?.includes('/auth/refresh');

    // Anything other than 401 on a protected endpoint → surface normalized error.
    if (status !== 401 || !original || original._retried || isAuthRoute) {
      throw ApiError.from(error);
    }

    // Mark to prevent infinite loops.
    original._retried = true;

    // If a refresh is already in flight, enqueue this request.
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token) => {
            (original.headers as Record<string, string>).Authorization = `Bearer ${token}`;
            api.request(original).then(resolve).catch(reject);
          },
          reject,
        });
      });
    }

    isRefreshing = true;
    try {
      const tokens = await runRefresh();
      bindings.setAccess(tokens.accessToken);
      setRefreshToken(tokens.refreshToken); // token rotation
      flushQueue(null, tokens.accessToken);

      (original.headers as Record<string, string>).Authorization = `Bearer ${tokens.accessToken}`;
      return await api.request(original);
    } catch (refreshErr) {
      const apiErr = ApiError.from(refreshErr);
      bindings.setAccess(null);
      setRefreshToken(null);
      flushQueue(apiErr, null);
      bindings.onFailure();
      throw apiErr;
    } finally {
      isRefreshing = false;
    }
  },
);
